import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, Lock } from 'lucide-react';

import { publicApi, ApiError } from '../lib/api.js';
import { Verify } from './Verify.js';
import { Review } from './Review.js';
import { Done } from './Done.js';

export interface PayInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  dueDate: string | null;
  amountCents: number;
  poNumber: string | null;
  description: string | null;
  hasPdf: boolean;
}

export interface SavedAccount {
  id: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  accountLast4: string;
  accountHolderName: string | null;
}

export interface PayData {
  customerName: string;
  noteToCustomer: string | null;
  totalAmountCents: number;
  expiresAt: string | null;
  invoices: PayInvoice[];
  savedAccounts: SavedAccount[];
  authorization: { id: string; version: number; body: string };
}

type State =
  | { status: 'loading' }
  /** The link itself is bad, expired, cancelled or locked — all indistinguishable. */
  | { status: 'dead'; message: string }
  | { status: 'challenge'; kind: 'total' | 'zip'; attemptsRemaining: number }
  | { status: 'ready'; data: PayData }
  | { status: 'done'; reference: string; amountCents: number };

/**
 * The customer's whole journey.
 *
 * Kept as one state machine because the steps share a token and a session and
 * because there is no route a customer should be able to deep-link into —
 * landing on the bank form without having answered the security question
 * should be impossible, not merely redirected away from.
 */
export function PayApp() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ status: 'loading' });

  const loadData = useCallback(async () => {
    try {
      const data = await publicApi.get<PayData>(`/api/pay/${token}`);
      setState({ status: 'ready', data });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_verified') {
        const challenge = await publicApi.get<{
          verificationKind: 'total' | 'zip';
          attemptsRemaining: number;
        }>(`/api/pay/${token}/challenge`);
        setState({
          status: 'challenge',
          kind: challenge.verificationKind,
          attemptsRemaining: challenge.attemptsRemaining,
        });
        return;
      }
      setState({
        status: 'dead',
        message:
          error instanceof ApiError ? error.message : 'We could not open this payment link.',
      });
    }
  }, [token]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4">
          <span className="text-lg font-bold tracking-tight text-gold-700">
            Gold Star Printing
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
            <Lock className="h-3.5 w-3.5" aria-hidden />
            Secure payment
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8">
        {state.status === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Loading…
          </div>
        )}

        {state.status === 'dead' && (
          <div className="card p-6">
            <h1 className="font-semibold text-slate-900">This link is no longer valid</h1>
            <p className="mt-2 text-slate-600">{state.message}</p>
          </div>
        )}

        {state.status === 'challenge' && (
          <Verify
            token={token!}
            kind={state.kind}
            attemptsRemaining={state.attemptsRemaining}
            onVerified={loadData}
            onDead={(message) => setState({ status: 'dead', message })}
          />
        )}

        {state.status === 'ready' && (
          <Review
            token={token!}
            data={state.data}
            onSubmitted={(reference) =>
              setState({
                status: 'done',
                reference,
                amountCents: state.data.totalAmountCents,
              })
            }
          />
        )}

        {state.status === 'done' && (
          <Done reference={state.reference} amountCents={state.amountCents} />
        )}
      </main>

      <footer className="mx-auto max-w-3xl px-5 pb-10 text-center text-xs text-slate-500">
        <p className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
          Your bank details are encrypted and are never sent by email.
        </p>
        <p className="mt-1">Gold Star Printing · 574-555-0100</p>
      </footer>
    </div>
  );
}
